import styled from '@emotion/styled'
import {
  ExtranetCustomerListItemDto,
  ExtranetCustomerUserRoleEnum,
} from '@pharmac-extranet/extranet-api'
import { BreakpointWidths, MediaQuery, useLanguage } from '@pharmac-extranet/shared'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AuthenticatedUserDto } from '../../../features/authentication/dto'
import { useAuthenticationContext } from '../../../features/authentication/useAuthentication'
import { useCustomerContext } from '../../../features/customer/CustomerProvider'
import { useLanguageContext } from '../../../i18n/context/useLanguageContext'
import { useAuthorizationFlags } from '../../../router/RouteGuards'
import { useExtranetPath } from '../../../router/useExtranetPath'
import { NavigationMenu, NavigationMenuItemProps } from '../menu/NavigationMenu'

const UserMenuRoot = styled.div`
  position: relative;

  margin-left: auto;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;

  z-index: 10;

  @media (max-width: ${BreakpointWidths.Desktop - 1}px) {
    width: 38px;
  }
`

const UserMenuCustomer = styled.div`
  height: 38px;
  min-width: 325px;

  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;

  padding-left: var(--spacing-medium);
  padding-right: 40px;

  margin-right: -16px;

  border-radius: var(--border-radius-medium);

  background-color: var(--color-primary);
  font-family: var(--font-family-secondary);
  font-size: var(--typo-text-medium-size);
  color: var(--color-white);

  z-index: 11;

  @media (max-width: ${BreakpointWidths.Desktop - 1}px) {
    display: none;
    min-width: 0px;
  }
`

const UserMenuAvatar = styled.div<{ isOpen?: boolean }>`
  border-radius: 18px;
  border: 1px solid var(--color-white);

  z-index: 20;

  margin-left: -12px;

  width: 38px;
  height: 38px;

  display: flex;
  justify-content: center;
  align-items: center;

  opacity: 1;

  background-color: var(--color-white);

  &:hover {
    cursor: pointer;
  }
`

const UserMenuInitials = styled.div`
  font-family: var(--font-family-secondary);
  font-size: var(--typo-h6-size);
  text-align: center;
  transform: translateY(1px);
  color: var(--color-primary);
  font-weight: 600;
`

const UserMenuDropdown = styled.div`
  position: absolute;

  top: calc(50% - 19px);
  right: 0;

  z-index: 10;

  background-color: var(--color-white);
  background: var(--extranet-menu-bg-gradient);
  border-radius: var(--border-radius-medium) var(--border-radius-medium) var(--border-radius-small)
    var(--border-radius-small);

  overflow: hidden;

  display: flex;
  flex-direction: column;

  width: 100%;

  ${MediaQuery.minMobile} {
    width: 90vw;
  }

  transition: opacity 0.3s var(--ease-cubic-bezier);
`

const UserMenuDropdownInner = styled.div`
  min-width: 275px;
  padding-top: 50px;
  padding-bottom: var(--spacing-small);

  @media (max-width: ${BreakpointWidths.Desktop - 1}px) {
    padding-top: var(--spacing-medium);
  }
`

const UserMenuClickLayer = ({ closeMenu }: { closeMenu: () => void }) => {
  return (
    <div
      onClick={closeMenu}
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        width: '100%',
        height: '100%',
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 9,
      }}
    />
  )
}

type UserMenuProps = {
  noCustomer?: boolean
}

export const UserMenu = (props: UserMenuProps) => {
  const [state, set] = useState(false)
  const navigate = useNavigate()

  const createLink = useExtranetPath()

  const { changeLanguage } = useLanguageContext()

  const { user, isPharmacCustomerService } = useAuthenticationContext()
  const {
    customer: customership,
    userCustomers: userCustomerships,
    setCustomer,
    roles,
  } = useCustomerContext()

  const { customerUserManagement } = useAuthorizationFlags()

  const { t, key, i18n } = useLanguage()

  const createExtranetPath = useExtranetPath()

  const userMenuLanguages: NavigationMenuItemProps[] = (() => {
    const items = []

    const lang = i18n.language

    if (lang !== 'fi') {
      items.push({
        label: key({ fi: 'Suomi', en: 'Finnish', sv: 'Finska' }),
        onClick: () => changeLanguage('fi'),
      })
    }

    if (lang !== 'en') {
      items.push({
        label: key({ fi: 'Englanti', en: 'English', sv: 'Engelska' }),
        onClick: () => changeLanguage('en'),
      })
    }

    if (lang !== 'sv') {
      items.push({
        label: key({ fi: 'Ruotsi', en: 'Swedish', sv: 'Svensk' }),
        onClick: () => changeLanguage('sv'),
      })
    }

    return items
  })()

  const createInitials = (user: AuthenticatedUserDto) => {
    const initials = user.firstName.substring(0, 1) + user.lastName.substring(0, 1)

    return initials.toUpperCase()
  }

  const handleChangeCustomer = (customer: ExtranetCustomerListItemDto) => {
    setCustomer(customer)

    navigate(createExtranetPath('root'))
  }

  if (!user) return null

  const createUserMenuItems = (): NavigationMenuItemProps[] => {
    const menuItems: NavigationMenuItemProps[] = [
      {
        label: user && `${user.firstName} ${user.lastName}`,
        icon: 'user',
        navigateTo: createExtranetPath('user'),
      },
    ]

    if (customership) {
      const invoiceViewing = roles?.roles.some(
        (role) => role.role === ExtranetCustomerUserRoleEnum.InvoiceViewing
      )
      if (customerUserManagement || isPharmacCustomerService || invoiceViewing) {
        const paths = []

        if (roles?.isAdmin || customerUserManagement) {
          paths.push({
            label: t('userMenu.infoAndUsers'),
            navigateTo: createExtranetPath('oma-annos.customership'),
          })
        }

        if (roles?.isAdmin || invoiceViewing)
          paths.push({
            label: t('shop.orders.shopOrders'),
            navigateTo: createExtranetPath('shop.orderHistory'),
          })

        if (customership.companyRole?.role === 'A01' && (roles?.isAdmin || invoiceViewing) ) {
          paths.push({
            label: t('customer.reports.reports'),
            navigateTo: createExtranetPath('oma-annos.customership.reports'),
          })
        }

        menuItems.push({
          label: t('userMenu.ownCustomership'),
          icon: 'users',
          children: paths,
        })
      }

      if (userCustomerships.length > 1) {
        menuItems.push({
          label: t('userMenu.changeCustomership'),
          icon: 'home',
          children: userCustomerships.map((userCustomership) => ({
            onClick: () => handleChangeCustomer(userCustomership),
            label: userCustomership.name,
            isActive: customership.id === userCustomership.id,
          })),
        })
      }
    }

    menuItems.push(
      {
        label: t('userMenu.selectLanguage'),
        icon: 'flag',
        children: userMenuLanguages,
      },
      {
        label: t('userMenu.logout'),
        icon: 'logOut',
        dividerBefore: true,
        navigateTo: createLink('logout'),
      }
    )

    return menuItems
  }

  return (
    <UserMenuRoot>
      {!props.noCustomer && !!customership && (
        <UserMenuCustomer>{customership.name}</UserMenuCustomer>
      )}
      <UserMenuAvatar
        onClick={() => set(!state)}
        isOpen={state}
      >
        <UserMenuInitials>{createInitials(user)}</UserMenuInitials>
      </UserMenuAvatar>

      <UserMenuDropdown
        style={{
          opacity: state ? 1 : 0,
          maxHeight: state ? 'fit-content' : '38px',
          maxWidth: state ? '335px' : '38px',
          boxShadow: state ? '4px 4px 5px 0 rgba(0, 0, 0, 0.03)' : 'none',
        }}
      >
        <UserMenuDropdownInner>
          <NavigationMenu
            clickCallback={() => set(false)}
            menu={createUserMenuItems()}
          />
        </UserMenuDropdownInner>
      </UserMenuDropdown>
      {state && <UserMenuClickLayer closeMenu={() => set(false)} />}
    </UserMenuRoot>
  )
}
